import type { Language } from "./translations";
import type { Database } from "@/integrations/supabase/types";

export type BlogCategoryRow = Database["public"]["Tables"]["blog_categories"]["Row"];
export type BlogPostRow = Database["public"]["Tables"]["blog_posts"]["Row"];

export interface LocalizedCategory {
  id: string;
  slug: string;
  name: string;
  accent_color: string | null;
}

export interface LocalizedPost {
  id: string;
  slug: string;
  category: LocalizedCategory | null;
  cover_image_url: string | null;
  author_name: string;
  reading_time_minutes: number | null;
  published_at: string | null;
  updated_at: string | null;
  external_url: string | null;
  title: string;
  excerpt: string;
  content: string;
  /** SEO-optimized <title> (<=60 chars). Falls back to `title` when unset. */
  seo_title: string;
  /** SEO meta description (140-160 chars). Falls back to `excerpt` when unset. */
  seo_description: string;
  /** FAQ pairs generated from the article content, in the requested locale. */
  faq: Array<{ q: string; a: string }>;
  /** Whether the post fell back to English (i.e. requested language was missing). */
  is_fallback: boolean;
  /** Whether an English version of the content exists (for translation toggle). */
  has_english: boolean;
}

const pickLocalized = (
  post: BlogPostRow,
  field: "title" | "excerpt" | "content",
  language: Language,
): { value: string; fallback: boolean } => {
  const primary = post[`${field}_${language}` as keyof BlogPostRow] as string | null;
  if (primary && primary.trim().length > 0) {
    return { value: primary, fallback: false };
  }
  // Fallback chain: EN, then any other available language
  const en = post.title_en !== undefined ? (post[`${field}_en` as keyof BlogPostRow] as string | null) : null;
  if (en && en.trim().length > 0) {
    return { value: en, fallback: true };
  }
  const fallbackLangs: Language[] = ["pl", "by"];
  for (const lang of fallbackLangs) {
    const v = post[`${field}_${lang}` as keyof BlogPostRow] as string | null;
    if (v && v.trim().length > 0) {
      return { value: v, fallback: true };
    }
  }
  return { value: "", fallback: true };
};

export const localizeCategory = (
  category: BlogCategoryRow | null | undefined,
  language: Language,
): LocalizedCategory | null => {
  if (!category) return null;
  const name = category[`name_${language}` as keyof BlogCategoryRow] as string;
  return {
    id: category.id,
    slug: category.slug,
    name: name || category.name_en,
    accent_color: category.accent_color,
  };
};

export const localizePost = (
  post: BlogPostRow & { blog_categories?: BlogCategoryRow | null },
  language: Language,
): LocalizedPost => {
  const title = pickLocalized(post, "title", language);
  const excerpt = pickLocalized(post, "excerpt", language);
  const content = pickLocalized(post, "content", language);
  const seoRow = post as BlogPostRow & Record<string, string | null | undefined>;
  const seoTitle = seoRow[`seo_title_${language}`] || seoRow.seo_title_en || title.value;
  const seoDescription =
    seoRow[`seo_description_${language}`] || seoRow.seo_description_en || excerpt.value;
  const faqRaw = (post as BlogPostRow & Record<string, unknown>)[`faq_${language}`];
  const faq: Array<{ q: string; a: string }> = Array.isArray(faqRaw)
    ? (faqRaw as Array<{ q?: unknown; a?: unknown }>)
        .filter((x) => x && typeof x.q === "string" && typeof x.a === "string")
        .map((x) => ({ q: x.q as string, a: x.a as string }))
    : [];
  return {
    faq,
    id: post.id,
    slug: post.slug,
    category: localizeCategory(post.blog_categories ?? null, language),
    cover_image_url: post.cover_image_url,
    author_name: post.author_name,
    reading_time_minutes: post.reading_time_minutes,
    published_at: post.published_at,
    updated_at: (post as BlogPostRow & { updated_at?: string | null }).updated_at ?? null,
    external_url: (post as BlogPostRow & { external_url?: string | null }).external_url ?? null,
    title: title.value,
    excerpt: excerpt.value,
    content: content.value,
    seo_title: seoTitle,
    seo_description: seoDescription,
    is_fallback: title.fallback || content.fallback,
    has_english: !!(post.content_en && post.content_en.trim().length > 0),
  };
};

// Month names are hard-coded on purpose: the server runtime ships limited
// ICU data, so toLocaleDateString would render English there and Belarusian /
// Polish in the browser, causing a hydration mismatch that re-renders the list.
const MONTHS: Record<Language, string[]> = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  pl: ["stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca", "lipca", "sierpnia", "września", "października", "listopada", "grudnia"],
  by: ["студзеня", "лютага", "сакавіка", "красавіка", "мая", "чэрвеня", "ліпеня", "жніўня", "верасня", "кастрычніка", "лістапада", "снежня"],
};

export const formatPostDate = (iso: string | null, language: Language): string => {
  if (!iso) return "";
  const date = new Date(iso);
  const d = date.getUTCDate();
  const m = MONTHS[language][date.getUTCMonth()] ?? "";
  const y = date.getUTCFullYear();
  if (language === "en") return `${m} ${d}, ${y}`;
  if (language === "pl") return `${d} ${m} ${y}`;
  return `${d} ${m} ${y} г.`;
};

export const blogBasePath = (language: Language) =>
  language === "pl" ? "/pl/blog" : language === "by" ? "/by/blog" : "/blog";
